import { useEffect, useState } from "react";
import { NumberInput } from "../../../components/numberInput/numberInput";
import { useParams } from "react-router";
import { AdminHeader } from "../../../components/adminHeader/adminHeader";
import { Button } from "../../../components/button/button";
import { shallowEqual } from "react-redux";
import { useAnimatedNavigate } from "../../../hooks/useAnimatedNavigate";
// import TableAPI from "../../../api/TableAPI";
import AdminAPI from "../../../api/AdminAPI";

import { Text } from "../../../components/text/text";

import styles from "./workerMotivationPage.module.css"

const defaultSettings = {
    salaryPerHour: 0,
    hoursPerShift: 0,
    countShiftsMonthly: 0,
    baseSalary: 0,
    bonusPercentage: 0,
    avgRevenue: 0,
};

const normalizeSettings = (data = {}) => ({
    salaryPerHour: data?.salaryPerHour ?? defaultSettings.salaryPerHour,
    hoursPerShift: data?.hoursPerShift ?? defaultSettings.hoursPerShift,
    countShiftsMonthly: data?.countShiftsMonthly ?? defaultSettings.countShiftsMonthly,
    baseSalary: data?.baseSalary ?? defaultSettings.baseSalary,
    bonusPercentage: data?.bonusPercentage ?? defaultSettings.bonusPercentage,
    avgRevenue: data?.avgRevenue ?? defaultSettings.avgRevenue,
});

const MotivationSetting = ({ val, setVal, label, error, ...params }) => {
    return (
        <div className={styles.motivation_setting}>
            <Text as="div" variant="caption" className={styles.motivation_setting_label} >{label}</Text>
            <NumberInput error={error} val={val} setVal={setVal} {...params} />
        </div>
    )
}

const initialSettings = (val) => ({
    salaryPerHour: val,
    hoursPerShift: val,
    countShiftsMonthly: val,
    baseSalary: val,
    bonusPercentage: val,
    avgRevenue: val,
});

export const WorkerMotivationPage = () => {
    const [settings, setSettings] = useState(defaultSettings);
    const [settingErrors, setSettingErrors] = useState(initialSettings(false));
    const [lastSettings, setLastSettings] = useState(defaultSettings);

    const { userId, cafeId } = useParams();

    useEffect(() => {
        if (!userId) {
            return;
        }

        let isUnmounted = false;

        AdminAPI
            .fetchUserMotivation(userId)
            .then(res => {
                if (isUnmounted) {
                    return;
                }

                const normalized = normalizeSettings(res);
                setSettings(normalized);
                setLastSettings(normalized);
            })
            .catch(error => {
                console.error(error);

                if (isUnmounted) {
                    return;
                }

                const normalized = normalizeSettings();
                setSettings(normalized);
                setLastSettings(normalized);
            });

        return () => {
            isUnmounted = true;
        };
    }, [userId]);

    // const [hours, setHours] = useState(0);
    // const [days, setDays] = useState(0);

    // useEffect(() => {
    //     TableAPI
    //         .fetchEmployeeTotalShifts(userId)
    //         .then(({ shifts, hours }) => {
    //             setHours(hours);
    //             setDays(shifts);
    //         })
    //         .catch(console.error);
    // }, [userId]);

    const validateData = (data) => {
        let error = false;
        const hoursPerShift = Number(data.hoursPerShift);
        const countShiftsMonthly = Number(data.countShiftsMonthly);
        const bonusPercentage = Number(data.bonusPercentage);

        if (!Number.isFinite(hoursPerShift) || hoursPerShift > 24 || hoursPerShift <= 0) {
            setSettingErrors(prev => ({ ...prev, hoursPerShift: true }));
            error = true;
        }

        if (!Number.isFinite(countShiftsMonthly) || countShiftsMonthly > 31 || countShiftsMonthly <= 0) {
            setSettingErrors(prev => ({ ...prev, countShiftsMonthly: true }));
            error = true;
        }

        if (!Number.isFinite(bonusPercentage) || bonusPercentage > 100 || bonusPercentage < 0) {
            setSettingErrors(prev => ({ ...prev, bonusPercentage: true }));
            error = true;
        }

        return error;
    }

    useEffect(() => {
        setSettingErrors(initialSettings(false));
    }, [settings]);

    const navigate = useAnimatedNavigate();
    const [loading, setLoading] = useState(false);

    const onSaveData = () => {
        setSettingErrors(initialSettings(false));

        if (!userId || !cafeId) {
            return;
        }

        const motivationPayload = normalizeSettings(settings);
        if (validateData(motivationPayload)) return;

        setLoading(true);
        AdminAPI
            .saveUserMotivation(userId, motivationPayload)
            .then(() => {
                setLastSettings(motivationPayload);
                navigate(`/admin/${cafeId}/cafe/employees/${userId}`);
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }

    const setSetting = (setting, value) => {
        setSettings(prev => ({ ...prev, [setting]: value }))
    }

    return (
        <>
            <AdminHeader title="План для:" userId={userId} />
            <Text as="div" variant="body" className="page_description" >
                Установите план для сотрудника, он отобразится в его профиле.
            </Text>

            <div className={styles.motivation_settings}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: "14px", columnGap: "10px", marginBottom: "75px" }}>
                    <MotivationSetting label="Кол-во смен в месяце" val={settings.countShiftsMonthly} setVal={(val) => setSetting("countShiftsMonthly", val)} error={settingErrors.countShiftsMonthly} />
                    <MotivationSetting label="Кол-во часов в смене" val={settings.hoursPerShift} setVal={(val) => setSetting("hoursPerShift", val)} error={settingErrors.hoursPerShift} />
                    <MotivationSetting label="Часовая ставка руб./час" val={settings.salaryPerHour} setVal={(val) => setSetting("salaryPerHour", val)} error={settingErrors.salaryPerHour} />
                    <MotivationSetting label="Оклад в месяц (если есть)" val={settings.baseSalary} setVal={(val) => setSetting("baseSalary", val)} error={settingErrors.baseSalary} />
                    <MotivationSetting label="Процент бонуса" val={settings.bonusPercentage} setVal={(val) => setSetting("bonusPercentage", val)} error={settingErrors.bonusPercentage} type="float" min={0} max={100} />
                    <MotivationSetting label="План по среднему чеку" val={settings.avgRevenue} setVal={(val) => setSetting("avgRevenue", val)} error={settingErrors.avgRevenue} />
                </div>
            </div>

            <Button
                className={styles.save_button}
                onClick={onSaveData}
                text="Сохранить"
                disabled={loading || shallowEqual(lastSettings, settings)}
            />
        </>
    )
}
